import { Button, Dropdown, Text } from "@minervaproject/fds";
import { range } from "lodash";
import pluralize from "pluralize";
import { useContext, useState } from "react";

import { createBreakoutHelper } from "api/api_helpers";
import { FlightAPIContext } from "contexts/flight_api_context";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import {
  BreakoutType,
  DropdownOptionType,
  PollSessionStatusType,
  PollTypeType,
} from "shared/types";
import { classroomSelectors, localSelectors } from "store";

function NewBreakoutForm({ breakouts }: { breakouts: BreakoutType[] }) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const section = useAppSelector(classroomSelectors.selectSection());
  const users = Object.values(useUsersProperties(PROFILE_PROPERTIES.concat("is_present")));
  const flightAPI = useContext(FlightAPIContext);
  const polls = Object.values(useAppSelector(classroomSelectors.selectPolls()));
  const pollSessions = Object.values(useAppSelector(classroomSelectors.selectPollSessions()));
  const currentlyBackingUpPoll = useAppSelector(localSelectors.selectCurrentlyBackingUpPoll());
  const [breakoutForm, setBreakoutForm] = useState({
    grouping: "groups:2",
    duration: "unlimited",
  });

  // TODO: section grouping + workbook assignment grouping
  const groupByGroupsOptions = range(2, 8).map((i) => ({
    label: `${i} groups`,
    value: `groups:${i}`,
  }));
  const groupByStudentsOptions = range(1, 10).map((i) => ({
    label: `${i} ${pluralize("student", i)} per group`,
    value: `groups-of:${i}`,
  }));
  const groupByPollOptions = polls
    .filter((poll) => {
      if (poll.type !== PollTypeType.LongForm) {
        return !!pollSessions.find(
          (pollSession) =>
            pollSession.poll_id === poll.id &&
            pollSession.status === PollSessionStatusType.Completed,
        );
      }
      return false;
    })
    .map((poll) => ({
      label: poll.title,
      value: `poll:${poll.id}-agree`,
    }));
  const groupingOptions = [
    {
      label: "By Groups",
      options: groupByGroupsOptions,
    },
    {
      label: "By Students",
      options: groupByStudentsOptions,
    },
    {
      label: "By Poll",
      options: groupByPollOptions,
    },
  ];

  if (!section.is_default) {
    groupingOptions.push({
      label: "By Section Groupings",
      options: [{ label: "Section groups", value: "section-groups" }],
    });
  }

  const durationOptions: DropdownOptionType[] = [1, 2, 5, 7, 10, 12, 15].map((minutes) => ({
    label: `Timed: ${minutes} ${pluralize("minute", minutes)}`,
    value: minutes * 60,
  }));
  durationOptions.push({
    label: "Unlimited time",
    value: "unlimited",
  });

  const handleChange = (field: string) => (option: DropdownOptionType) => {
    setBreakoutForm({ ...breakoutForm, [field]: option.value });
  };

  const handleCreateAndStart = async () => {
    try {
      const serverMilliseconds = flightAPI.getServerMilliseconds();
      await createBreakoutHelper(
        classId,
        breakouts,
        breakoutForm.grouping,
        breakoutForm.duration,
        users,
        currentlyBackingUpPoll,
        serverMilliseconds,
        dispatch,
        forumAPI,
      );
    } catch (e) {
      // TODO
    }
  };

  return (
    <>
      <Text className="body-l" kind="h2" mb={4}>
        Create a New Breakout
      </Text>
      <form className="flex flex-column">
        <Dropdown
          options={groupingOptions}
          onChange={handleChange("grouping")}
          defaultValue={groupByGroupsOptions[0]}
          isSearchable={false}
          fullWidth
          showDropdown
          className="text-black-tint-10"
          mb={2}
        />
        <Dropdown
          options={durationOptions}
          onChange={handleChange("duration")}
          defaultValue={durationOptions[durationOptions.length - 1]}
          isSearchable={false}
          fullWidth
          showDropdown
          className="text-black-tint-10"
          mb={2}
        />
        <Button
          kind="classroom-primary"
          data-testid="create-breakout-button"
          onClick={handleCreateAndStart}
          mt={5}
        >
          Create and Start
        </Button>
      </form>
    </>
  );
}

export { NewBreakoutForm };
